import type { AuthoredEntityOrSingleSchema } from '@cli-types/core-engine/add-field.js'
import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { EntitySingleLayout, SchemaMetadata } from '@cli-types/next-adapter/commands.js'
import { CREATE_PROMPT_CONTEXT } from '@cli-constants/prompt-copy.js'
import { promptSelectValue } from '../schema-prompts/prompt-select-value.js'
import { createSchemaTitleField } from './create-schema-title-field.js'
import { promptAdditionalSchemaFields } from './prompt-additional-schema-fields.js'
import { promptSchemaSlotFields } from './prompt-schema-slot-fields.js'
import { promptSchemaTabsField } from './prompt-schema-tabs-field.js'

export async function createEntityOrSingleSchema(
  kind: 'entity' | 'single',
  metadata: SchemaMetadata,
  schemasDir: string
): Promise<AuthoredEntityOrSingleSchema> {
  const titleField = createSchemaTitleField(metadata.titleLabel)
  const layout = await promptSelectValue<EntitySingleLayout>(
    CREATE_PROMPT_CONTEXT,
    'Schema layout',
    [
      { value: 'fields', label: 'Single fields array' },
      { value: 'slot', label: 'Main/sidebar slots' },
      { value: 'tabs', label: 'Top-level tabs' }
    ]
  )

  const schema: AuthoredEntityOrSingleSchema = {
    name: metadata.name,
    type: kind,
    label: metadata.label,
    ...(metadata.labelSingular ? { labelSingular: metadata.labelSingular } : {}),
    ...(metadata.labelPlural ? { labelPlural: metadata.labelPlural } : {}),
    description: metadata.description,
    icon: metadata.icon,
    ...(kind === 'entity'
      ? { columns: [{ accessorKey: 'title', header: metadata.titleLabel, type: 'text' as const }] }
      : {})
  }

  if (layout === 'slot') {
    schema.slot = await promptSchemaSlotFields(kind, schemasDir, titleField)
    return schema
  }

  if (layout === 'tabs') {
    schema.fields = [await promptSchemaTabsField(kind, schemasDir, titleField)]
    return schema
  }

  const fields: SchemaField[] = [titleField]
  await promptAdditionalSchemaFields(kind, schemasDir, fields)
  schema.fields = fields
  return schema
}
